import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, inject, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { SuggestedPointsService } from './services/suggested-points.service';
import { TranslateModule } from '@ngx-translate/core';
import { DragHandleComponent } from 'src/app/shared/drag-handle/drag-handle.component';
import { SearchEngineComponent } from "./components/search-engine/search-engine.component";
import { CardComponent } from "./components/card/card.component";
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatBottomSheetAddNewPointOrReplacePointData } from 'src/app/interfaces/mat.interface';
import { EditedStatus } from '../../interfaces/edit-route.interface';
import { Datasource, UiScrollModule } from 'ngx-ui-scroll';
import { MatBottomSheetService } from 'src/app/services/mat-bottom-sheet.service';
import { InterestFilterComponent } from './components/interest-filter/interest-filter.component';

@Component({
  selector: 'app-suggested-points',
  standalone: true,
  imports: [
    TranslateModule,
    DragHandleComponent,
    SearchEngineComponent,
    CardComponent,
    InterestFilterComponent,
    UiScrollModule
],
  templateUrl: './suggested-points.component.html',
  styleUrls: ['./suggested-points.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None
})
export class SuggestedPointsComponent {
  @ViewChild('suggestedPointsHeader', { static: true }) suggestedPointsHeaderRef!: ElementRef<HTMLElement>;
  @ViewChild('suggestedViewport', { static: true }) suggestedViewportRef!: ElementRef<HTMLElement>;

  datasource = new Datasource({
    get: (index, count, success) => {
      const data = [];
      for (let i = index; i <= index + count - 1; i++) {
        if (this.filteredSuggestedPoints?.length > i)
          data.push(this.filteredSuggestedPoints[i]);
      }
      success(data);
    },
    settings: {
      horizontal: false,
      minIndex: 0,
      startIndex: 0,
      padding: 0,
      viewportElement: () => this.suggestedViewportRef.nativeElement
    }
  });

  public dataAddNewPointOrReplacePoint: MatBottomSheetAddNewPointOrReplacePointData = inject(MAT_BOTTOM_SHEET_DATA)
  private _suggestedPointsService = inject(SuggestedPointsService);
  private _matBottomSheetService = inject(MatBottomSheetService)
  
  private readonly _suggestedPoints = computed(() => this._suggestedPointsService.suggestedPoints());
  get suggestedPoints() {
    return this._suggestedPoints();
  }
  
  private readonly _filteredSuggestedPoints = computed(() => this._suggestedPointsService.filteredSuggestedPoints());
  get filteredSuggestedPoints() {
    return this._filteredSuggestedPoints();
  }

  private readonly _availableInterestList = computed(() => this._suggestedPointsService.interestList());
  get availableInterestList() {
    return this._availableInterestList();
  }
  
  public searchTerm = signal('');
  public interestsIds = signal([]);

  public noResultsFoundMessage = 'No se han encontrado resultados';

  applyHeight = effect(() => {
    const size = this._matBottomSheetService.containerHeightSignal();
    if (!size){
      this.suggestedViewportRef.nativeElement.style.height = `${window.innerHeight * 0.8}px`;
      this.datasource.adapter.check();
      return;
    }

    const headerHeight = this.suggestedPointsHeaderRef.nativeElement.offsetHeight;
    this.suggestedViewportRef.nativeElement.style.height = `${size - headerHeight}px`;
    this.datasource.adapter.check();
  })
    
  updateSuggestedPointsAndInterestsIdsSelected = effect(() => {
    let suggestedPoints = this._suggestedPoints();
    const searchTerm = this.searchTerm();
    const interestsIds = this.interestsIds();
    
    // filter suggested points by end date of the previous point to be replaced
    if(this.dataAddNewPointOrReplacePoint.status === EditedStatus.UPDATED) {
      suggestedPoints = this._suggestedPointsService.filterSuggestedPointsByEndDatePreviousPoint(
        suggestedPoints,
        this.dataAddNewPointOrReplacePoint.pointToBeReplaced,
        this.dataAddNewPointOrReplacePoint.dayToShowIndex
      )
    }
    
    // filter out already selected points from the suggested points
    suggestedPoints = this._suggestedPointsService.filterSuggestedPointsByAlreadySelectedPoints(suggestedPoints);

    // filter by search term from the suggested points
    suggestedPoints = this._suggestedPointsService.applySearchTermFilter(searchTerm, suggestedPoints);
    
    // Apply interest Ids filter to the cleaned suggested points
    this._suggestedPointsService.applyInterestsIdsFilter(interestsIds, suggestedPoints);
    this.datasource.adapter.reload(); 
  }, { allowSignalWrites: true });

  ngAfterViewInit() {
    setTimeout(() => {
      const headerHeight = this.suggestedPointsHeaderRef.nativeElement.offsetHeight;
      this.suggestedViewportRef.nativeElement.style.height = `${(window.innerHeight * 0.8) - headerHeight}px`;
      this.datasource.adapter.check();
    }, 0);
  }
   
  handleSearchTermChange(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
  }

  handleInterestsIdsChange(ids: number[]): void {
    this.interestsIds.set(ids);
  }
}
